<template>
  <div class="custom-fields-popup">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        data-cy="custom-fields-dialog"
        class="flex max-h-[90vh] w-[920px] max-w-[95vw] flex-col gap-0 overflow-hidden p-0 !top-[5vh] !translate-y-0"
        @open-auto-focus.prevent
        @pointer-down-outside.prevent
        @focus-outside.prevent
      >
        <SDialogHeader class="shrink-0 border-b px-6 py-4 text-left">
          <SDialogTitle class="text-lg font-semibold">
            Custom fields
          </SDialogTitle>
        </SDialogHeader>
        <div class="flex min-h-0 flex-1 flex-col">
          <div
            v-if="
              bookingTypeCustomFields.length || ticketTypeCustomFields.length
            "
            class="flex shrink-0 border-b"
          >
            <button
              type="button"
              v-if="bookingTypeCustomFields.length"
              @click="selectBookingTypeCustomFields"
              data-cy="custom-fields-booking-tab"
              class="-mb-px flex-1 cursor-pointer border-b-2 py-3 text-center text-sm font-medium transition"
              :class="
                displayedCustomFieldsType === CustomFieldType.BOOKING
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              "
            >
              {{ $t("custom-fields.booking") }}
            </button>
            <button
              type="button"
              v-if="ticketTypeCustomFields.length"
              @click="selectTicketTypeCustomFields"
              data-cy="custom-fields-tickets-tab"
              class="-mb-px flex-1 cursor-pointer border-b-2 py-3 text-center text-sm font-medium transition"
              :class="
                displayedCustomFieldsType === CustomFieldType.TICKET
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              "
            >
              {{ $t("custom-fields.individual-tickets") }}
            </button>
          </div>

          <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div class="mx-auto max-w-[560px]">
              <!-- CUSTOM FIELDS OF TYPE BOOKING -->
              <div
                v-if="
                  computedDisplayedCustomFieldsType === CustomFieldType.BOOKING
                "
                class="space-y-6"
              >
                <div
                  v-for="cust in bookingTypeCustomFields"
                  :key="cust.uuid"
                  data-cy="cf-field"
                  :data-cy-field="cust.customField.name"
                >
                  <div>
                    <label
                      class="mb-1.5 block text-sm font-medium text-foreground"
                    >
                      {{ cust.customField.name }}
                      <span
                        v-if="
                          cust.customField.validation ===
                          CustomFieldValidation.MANDATORY
                        "
                        class="text-destructive"
                      >
                        *
                      </span>
                    </label>

                    <!-- phone number -->
                    <div
                      v-if="
                        cust.customField.informationType ===
                        InformationType.PHONE
                      "
                    >
                      <MazPhoneNumberInput
                        data-cy="cf-phone"
                        v-model="cust.answer"
                        color="info"
                        show-code-on-list
                        :default-country-code="
                          getCountryCodeFromPhone(cust) || userCurrentCountry
                        "
                        list-position="top left"
                        @update="results = $event"
                        :success="results?.isValid"
                        size="md"
                      />
                    </div>
                    <!-- phone number -->

                    <!-- File, Input, Textarea, Country selector, Select, Calendar -->
                    <div
                      v-else-if="
                        cust.customField.questionType !== QuestionType.LIST &&
                        cust.customField.questionType !== QuestionType.RADIO
                      "
                    >
                      <!-- File -->
                      <div
                        v-if="
                          cust.customField.questionType === QuestionType.FILE
                        "
                      >
                        <div
                          class="relative w-fit"
                          v-if="
                            !cust.fileUrl /*No file yet, we show the input*/ ||
                            cust.isCropped /*there is cropped file we preview it*/
                          "
                        >
                          <label class="block cursor-pointer">
                            <input
                              v-if="!cust.fileUrl"
                              class="hidden"
                              :id="cust.uuid"
                              data-cy="file-input"
                              type="file"
                              name="resume"
                              :ref="cust.uuid"
                              accept="image/*"
                              @change="
                                handleFileUpload(
                                  $event,
                                  cust.uuid,
                                  CustomFieldType.BOOKING
                                )
                              "
                            />
                            <div
                              v-if="!cust.fileUrl"
                              data-cy="upload-button"
                              class="flex h-[132px] w-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-input bg-muted/30 text-muted-foreground transition hover:border-ring hover:bg-muted/50"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              >
                                <path
                                  d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                                />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" x2="12" y1="3" y2="15" />
                              </svg>
                              <span class="text-sm">Upload file</span>
                            </div>
                            <div
                              v-if="cust.fileUrl && cust.isCropped"
                              class="overflow-hidden rounded-lg border border-input"
                            >
                              <img
                                class="h-[132px] w-[132px] object-cover"
                                :src="cust.fileUrl"
                                alt="File uploaded"
                              />
                            </div>
                          </label>
                          <span
                            v-if="cust.isInvalid"
                            class="mt-1 block text-sm text-destructive"
                            >{{ cust.validationMessage }}</span
                          >
                          <button
                            v-if="cust.fileUrl"
                            type="button"
                            @click="removeFile(cust)"
                            class="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full border bg-background shadow-sm transition hover:bg-muted"
                          >
                            <SvgIcon
                              :iconContent="binIcon"
                              :iconAlt="binAlt"
                              :iconStyle="binIconStyle"
                            />
                          </button>
                        </div>
                        <div
                          v-if="
                            cust.fileUrl &&
                            (cust.customField.informationType ===
                              InformationType.PASSPORT ||
                              cust.customField.informationType ===
                                InformationType.PICTURE) &&
                            !cust.isCropped
                          "
                          style="
                            margin-bottom: 16px;
                            display: flex;
                            flex-direction: column;
                            gap: 12px;
                            width: fit-content;
                            max-width: 100%;
                          "
                          :ref="cust.uuid + '_cropper_parent'"
                        >
                          <div
                            :style="{
                              width:
                                cust.customField.informationType ===
                                InformationType.PASSPORT
                                  ? '280px'
                                  : '320px',
                              height:
                                cust.customField.informationType ===
                                InformationType.PASSPORT
                                  ? '360px'
                                  : '320px',
                              maxWidth: '100%',
                              maxHeight: '60vh',
                            }"
                          >
                            <SmeetzCropper
                              data-cy="image-cropper"
                              v-if="cust.fileUrl"
                              :ref="cust.uuid + '_cropper'"
                              :src="cust.fileUrl"
                              :aspect-ratio="
                                cust.customField.informationType ===
                                InformationType.PASSPORT
                                  ? 3.5 / 4.5
                                  : 1
                              "
                              :view-mode="1"
                            />
                          </div>
                          <div
                            class="d-flex confirm-button-container"
                            style="margin-top: 0px"
                          >
                            <SButton
                              type="button"
                              variant="destructive"
                              @click="cancelCroppedPicture(cust)"
                              data-cy="cropper-remove-button"
                            >
                              {{ $t("button.remove") }}
                            </SButton>
                            <SButton
                              type="button"
                              @click="confirmCroppedPicture(cust)"
                              data-cy="cropper-confirm-button"
                            >
                              {{ $t("button.save") }}
                            </SButton>
                          </div>
                        </div>
                      </div>
                      <!-- File -->

                      <!-- Input -->
                      <SInput
                        data-cy="cf-text"
                        v-if="
                          cust.customField.questionType === QuestionType.TEXT &&
                          cust.customField.informationType !==
                            InformationType.COUNTRY &&
                          cust.customField.informationType !==
                            InformationType.EMAIL
                        "
                        class="w-full"
                        :placeholder="cust.customField.answerExample || ''"
                        v-model="cust.answer"
                      />
                      <SInput
                        data-cy="cf-text"
                        v-if="
                          cust.customField.questionType === QuestionType.TEXT &&
                          cust.customField.informationType !==
                            InformationType.COUNTRY &&
                          cust.customField.informationType ===
                            InformationType.EMAIL
                        "
                        class="w-full"
                        :placeholder="cust.customField.answerExample || ''"
                        v-model="cust.answer"
                        :aria-invalid="
                          (cust.answer &&
                            isEmailValid(cust).isValid === false) ||
                          undefined
                        "
                      />
                      <span
                        class="mt-1 block text-sm text-destructive"
                        data-cy="cf-invalid-email"
                        v-if="
                          cust.answer && isEmailValid(cust).isValid === false
                        "
                      >
                        {{ $t("custom-fields.invalid-email") }}
                      </span>
                      <!-- Input -->

                      <!-- Textarea -->
                      <STextarea
                        data-cy="cf-textarea"
                        v-if="
                          cust.customField.questionType ===
                          QuestionType.LONG_TEXT
                        "
                        v-model="cust.answer"
                        :rows="2"
                        class="w-full"
                        :placeholder="cust.customField.answerExample || ''"
                      />
                      <!-- Textarea -->

                      <!-- COUNTRY Selector -->
                      <div
                        v-else-if="
                          cust.customField.informationType ===
                          InformationType.COUNTRY
                        "
                        data-cy="cf-country"
                      >
                        <SCountrySelect
                          v-model="cust.answer"
                          :lang="appLanguage"
                          :priority-country-codes="[
                            'DE',
                            'US',
                            'GB',
                            'FR',
                            'IT',
                          ]"
                          :priority-label="
                            $t('custom-fields.frequently-selected')
                          "
                          :countries-label="$t('custom-fields.all-countries')"
                          :placeholder="$t('custom-fields.select-country')"
                        />
                      </div>
                      <!-- COUNTRY Selector -->

                      <!-- LANGUAGE Selector -->
                      <SSelect
                        v-else-if="
                          cust.customField.informationType ===
                          InformationType.LANGUAGE
                        "
                        v-model="cust.answer"
                      >
                        <SSelectTrigger class="w-full" data-cy="cf-select">
                          <SSelectValue :placeholder="$t('button.select')" />
                        </SSelectTrigger>
                        <SSelectContent>
                          <SSelectItem
                            v-for="item in languagesList(cust.customField.list)"
                            :key="item.key"
                            :value="item.key"
                          >
                            {{ item.value }}
                          </SSelectItem>
                        </SSelectContent>
                      </SSelect>
                      <!-- LANGUAGE Selector -->

                      <!-- Select -->
                      <SSelect
                        v-else-if="
                          cust.customField.questionType ===
                          QuestionType.DROPDOWN
                        "
                        v-model="cust.answer"
                      >
                        <SSelectTrigger class="w-full" data-cy="cf-select">
                          <SSelectValue :placeholder="$t('button.select')" />
                        </SSelectTrigger>
                        <SSelectContent>
                          <SSelectItem
                            v-for="item in cust.customField.list"
                            :key="item"
                            :value="item"
                          >
                            {{ item }}
                          </SSelectItem>
                        </SSelectContent>
                      </SSelect>
                      <!-- Selector -->

                      <!-- Calendar -->
                      <div
                        v-else-if="
                          cust.customField.questionType === QuestionType.DATE
                        "
                        data-cy="cf-date"
                      >
                        <SDatePicker
                          :model-value="dateAnswerValue(cust.answer)"
                          display-format="YYYY-MM-DD"
                          trigger-class="w-full"
                          :placeholder="$t('customer.birthdate-placeholder')"
                          @update:model-value="
                            (value) =>
                              (cust.answer = value ? value.toString() : '')
                          "
                        />
                      </div>
                      <!-- Calendar -->
                    </div>
                    <!-- File, Input, Textarea, Country selector, Select, Calendar -->

                    <!-- unique checkbox radio -->
                    <div
                      v-else-if="
                        cust.customField.questionType === QuestionType.RADIO
                      "
                    >
                      <SRadioGroup
                        data-cy="cf-radio"
                        v-model="cust.answer"
                        class="flex flex-col gap-2"
                      >
                        <div
                          v-for="(item, index) in cust.customField.list"
                          :key="item"
                          class="flex items-center gap-2"
                        >
                          <SRadioGroupItem
                            :data-cy-option="stripHtml(item)"
                            :id="`radio-${cust.uuid}-${index}`"
                            :value="item"
                          />
                          <SLabel :for="`radio-${cust.uuid}-${index}`">
                            {{ item }}
                          </SLabel>
                        </div>
                      </SRadioGroup>
                    </div>
                    <!-- unique checkbox radio -->

                    <!-- list of checkbox -->
                    <div
                      v-else-if="
                        cust.customField.questionType === QuestionType.LIST
                      "
                    >
                      <div data-cy="cf-list" class="flex flex-col gap-2">
                        <label
                          v-for="item in cust.customField.list"
                          :key="item"
                          class="flex cursor-pointer items-center gap-2"
                        >
                          <SCheckbox
                            :data-cy-option="stripHtml(item)"
                            :model-value="isListChecked(cust, item)"
                            @update:model-value="
                              (checked) => toggleListAnswer(cust, item, checked)
                            "
                          />
                          <span>{{ stripHtml(item) }}</span>
                        </label>
                      </div>
                    </div>
                    <!-- checkbox -->
                    <span
                      class="mt-1 block text-sm text-destructive"
                      data-cy="cf-required-error"
                      v-if="
                        !cust.answer &&
                        cust.customField.validation ===
                          CustomFieldValidation.MANDATORY &&
                        canValidate
                      "
                    >
                      {{ $t("custom-fields.required-field") }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- CUSTOM FIELDS OF TYPE TICKETS -->
              <div
                v-if="
                  computedDisplayedCustomFieldsType === CustomFieldType.TICKET
                "
                class="space-y-6"
              >
                <div
                  v-if="priceCategoryData.length > 1"
                  class="flex flex-wrap gap-2"
                >
                  <button
                    v-for="priceCategory in priceCategoryData"
                    :key="
                      priceCategory.categoryId + '_' + priceCategory.priceId
                    "
                    type="button"
                    @click="selectPriceCategory(priceCategory)"
                    class="cursor-pointer rounded-full border px-3 py-1.5 text-sm transition"
                    :class="
                      selectedPriceCategory.priceId === priceCategory.priceId &&
                      selectedPriceCategory.categoryId ===
                        priceCategory.categoryId
                        ? 'border-primary bg-primary/10 font-medium text-foreground'
                        : 'border-input text-muted-foreground hover:bg-muted/60'
                    "
                  >
                    {{
                      priceCategory.categoryName + " " + priceCategory.priceName
                    }}
                  </button>
                </div>
                <div
                  v-for="(
                    groupedFields, index
                  ) in groupedTicketTypeCustomFieldsByPSS.filter(
                    (groupedF) =>
                      groupedF.customFields.some(
                        (cust) =>
                          cust.priceId === selectedPriceCategory.priceId &&
                          cust.categoryId === selectedPriceCategory.categoryId
                      ) === true
                  )"
                  :key="groupedFields.pssUuid"
                  data-cy="cf-ticket-group"
                  :data-cy-ticket="index + 1"
                  class="space-y-5"
                  :class="{ 'border-t border-border pt-6': index > 0 }"
                >
                  <p class="text-base font-semibold text-foreground">
                    Ticket {{ index + 1 }}
                  </p>
                  <div
                    v-for="cust in groupedFields.customFields"
                    :key="cust.priceId + '_' + cust.categoryId"
                    data-cy="cf-field"
                    :data-cy-field="cust.customField.name"
                  >
                    <div>
                      <label
                        class="mb-1.5 block text-sm font-medium text-foreground"
                      >
                        {{ cust.customField.name
                        }}<span
                          v-if="
                            cust.customField.validation ===
                            CustomFieldValidation.MANDATORY
                          "
                          class="text-destructive"
                        >
                          *
                        </span>
                      </label>

                      <!-- phone number -->
                      <div
                        v-if="
                          cust.customField.informationType ===
                          InformationType.PHONE
                        "
                      >
                        <MazPhoneNumberInput
                          data-cy="cf-phone"
                          v-model="cust.answer"
                          color="info"
                          show-code-on-list
                          :default-country-code="
                            getCountryCodeFromPhone(cust) || userCurrentCountry
                          "
                          list-position="top left"
                          @update="results = $event"
                          :success="results?.isValid"
                          size="md"
                        />
                      </div>
                      <!-- phone number -->

                      <!-- File, Input, Textarea, Country selector, Select, Calendar -->
                      <div
                        v-else-if="
                          cust.customField.questionType !== QuestionType.LIST &&
                          cust.customField.questionType !== QuestionType.RADIO
                        "
                      >
                        <!-- File -->
                        <div
                          v-if="
                            cust.customField.questionType === QuestionType.FILE
                          "
                        >
                          <div
                            class="relative w-fit"
                            v-if="
                              !cust.fileUrl /*No file yet, we show the input*/ ||
                              cust.isCropped /*there is cropped file we preview it*/
                            "
                          >
                            <label class="block cursor-pointer">
                              <input
                                v-if="!cust.fileUrl"
                                class="hidden"
                                :id="cust.uuid"
                                data-cy="file-input"
                                type="file"
                                name="resume"
                                :ref="cust.uuid"
                                accept="image/*"
                                @change="
                                  handleFileUpload(
                                    $event,
                                    cust.uuid,
                                    CustomFieldType.TICKET
                                  )
                                "
                              />
                              <div
                                v-if="!cust.fileUrl"
                                data-cy="upload-button"
                                class="flex h-[132px] w-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-input bg-muted/30 text-muted-foreground transition hover:border-ring hover:bg-muted/50"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="22"
                                  height="22"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="1.8"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                >
                                  <path
                                    d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                                  />
                                  <polyline points="17 8 12 3 7 8" />
                                  <line x1="12" x2="12" y1="3" y2="15" />
                                </svg>
                                <span class="text-sm">Upload file</span>
                              </div>
                              <div
                                v-if="cust.fileUrl && cust.isCropped"
                                class="overflow-hidden rounded-lg border border-input"
                              >
                                <img
                                  class="h-[132px] w-[132px] object-cover"
                                  :src="cust.fileUrl"
                                  alt="File uploaded"
                                />
                              </div>
                            </label>
                            <span
                              v-if="cust.isInvalid"
                              class="mt-1 block text-sm text-destructive"
                              >{{ cust.validationMessage }}</span
                            >
                            <button
                              v-if="cust.fileUrl"
                              type="button"
                              @click="removeFile(cust)"
                              class="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full border bg-background shadow-sm transition hover:bg-muted"
                            >
                              <SvgIcon
                                :iconContent="binIcon"
                                :iconAlt="binAlt"
                                :iconStyle="binIconStyle"
                              />
                            </button>
                          </div>
                          <div
                            v-if="
                              cust.fileUrl &&
                              (cust.customField.informationType ===
                                InformationType.PASSPORT ||
                                cust.customField.informationType ===
                                  InformationType.PICTURE) &&
                              !cust.isCropped
                            "
                            style="
                              margin-bottom: 16px;
                              display: flex;
                              flex-direction: column;
                              gap: 12px;
                              width: fit-content;
                              max-width: 100%;
                            "
                            :ref="cust.uuid + '_cropper_parent'"
                          >
                            <div
                              :style="{
                                width:
                                  cust.customField.informationType ===
                                  InformationType.PASSPORT
                                    ? '280px'
                                    : '320px',
                                height:
                                  cust.customField.informationType ===
                                  InformationType.PASSPORT
                                    ? '360px'
                                    : '320px',
                                maxWidth: '100%',
                                maxHeight: '60vh',
                              }"
                            >
                              <SmeetzCropper
                                data-cy="image-cropper"
                                v-if="cust.fileUrl"
                                :ref="cust.uuid + '_cropper'"
                                :src="cust.fileUrl"
                                :aspect-ratio="
                                  cust.customField.informationType ===
                                  InformationType.PASSPORT
                                    ? 3.5 / 4.5
                                    : 1
                                "
                                :view-mode="1"
                              />
                            </div>
                            <div
                              class="d-flex confirm-button-container"
                              style="margin-top: 0px"
                            >
                              <SButton
                                type="button"
                                variant="destructive"
                                @click="cancelCroppedPicture(cust)"
                                data-cy="cropper-remove-button"
                              >
                                {{ $t("button.remove") }}
                              </SButton>
                              <SButton
                                type="button"
                                data-cy="cropper-confirm-button"
                                @click="confirmCroppedPicture(cust)"
                              >
                                {{ $t("button.save") }}
                              </SButton>
                            </div>
                          </div>
                        </div>
                        <!-- File -->

                        <!-- Input -->
                        <SInput
                          data-cy="cf-text"
                          v-if="
                            cust.customField.questionType ===
                              QuestionType.TEXT &&
                            cust.customField.informationType !==
                              InformationType.COUNTRY &&
                            cust.customField.informationType !==
                              InformationType.EMAIL
                          "
                          class="w-full"
                          :placeholder="cust.customField.answerExample || ''"
                          v-model="cust.answer"
                        />
                        <SInput
                          data-cy="cf-text"
                          v-if="
                            cust.customField.questionType ===
                              QuestionType.TEXT &&
                            cust.customField.informationType !==
                              InformationType.COUNTRY &&
                            cust.customField.informationType ===
                              InformationType.EMAIL
                          "
                          class="w-full"
                          :placeholder="cust.customField.answerExample || ''"
                          v-model="cust.answer"
                          :aria-invalid="
                            (cust.answer &&
                              isEmailValid(cust).isValid === false) ||
                            undefined
                          "
                        />
                        <span
                          class="mt-1 block text-sm text-destructive"
                          data-cy="cf-invalid-email"
                          v-if="
                            cust.answer && isEmailValid(cust).isValid === false
                          "
                        >
                          {{ $t("custom-fields.invalid-email") }}
                        </span>
                        <!-- Input -->

                        <!-- Textarea -->
                        <STextarea
                          data-cy="cf-textarea"
                          v-if="
                            cust.customField.questionType ===
                            QuestionType.LONG_TEXT
                          "
                          v-model="cust.answer"
                          :rows="2"
                          class="w-full"
                          :placeholder="cust.customField.answerExample || ''"
                        />
                        <!-- Textarea -->

                        <!-- COUNTRY Selector -->
                        <div
                          v-else-if="
                            cust.customField.informationType ===
                            InformationType.COUNTRY
                          "
                          data-cy="cf-country"
                        >
                          <SCountrySelect
                            v-model="cust.answer"
                            :lang="appLanguage"
                            :priority-country-codes="[
                              'DE',
                              'US',
                              'GB',
                              'FR',
                              'IT',
                            ]"
                            :priority-label="
                              $t('custom-fields.frequently-selected')
                            "
                            :countries-label="$t('custom-fields.all-countries')"
                            :placeholder="$t('custom-fields.select-country')"
                          />
                        </div>
                        <!-- COUNTRY Selector -->

                        <!-- LANGUAGE Selector -->
                        <SSelect
                          v-else-if="
                            cust.customField.informationType ===
                            InformationType.LANGUAGE
                          "
                          v-model="cust.answer"
                        >
                          <SSelectTrigger class="w-full" data-cy="cf-select">
                            <SSelectValue :placeholder="$t('button.select')" />
                          </SSelectTrigger>
                          <SSelectContent>
                            <SSelectItem
                              v-for="item in languagesList(
                                cust.customField.list
                              )"
                              :key="item.key"
                              :value="item.key"
                            >
                              {{ item.value }}
                            </SSelectItem>
                          </SSelectContent>
                        </SSelect>
                        <!-- LANGUAGE Selector -->

                        <!-- Select -->
                        <SSelect
                          v-else-if="
                            cust.customField.questionType ===
                            QuestionType.DROPDOWN
                          "
                          v-model="cust.answer"
                        >
                          <SSelectTrigger class="w-full" data-cy="cf-select">
                            <SSelectValue :placeholder="$t('button.select')" />
                          </SSelectTrigger>
                          <SSelectContent>
                            <SSelectItem
                              v-for="item in cust.customField.list"
                              :key="item"
                              :value="item"
                            >
                              {{ item }}
                            </SSelectItem>
                          </SSelectContent>
                        </SSelect>
                        <!-- Selector -->

                        <!-- Calendar -->
                        <div
                          v-else-if="
                            cust.customField.questionType === QuestionType.DATE
                          "
                          data-cy="cf-date"
                        >
                          <SDatePicker
                            :model-value="dateAnswerValue(cust.answer)"
                            display-format="YYYY-MM-DD"
                            trigger-class="w-full"
                            :placeholder="$t('customer.birthdate-placeholder')"
                            @update:model-value="
                              (value) =>
                                (cust.answer = value ? value.toString() : '')
                            "
                          />
                        </div>
                        <!-- Calendar -->
                      </div>
                      <!-- File, Input, Textarea, Country selector, Select, Calendar -->

                      <!-- unique checkbox radio -->
                      <div
                        v-else-if="
                          cust.customField.questionType === QuestionType.RADIO
                        "
                      >
                        <SRadioGroup
                          data-cy="cf-radio"
                          v-model="cust.answer"
                          class="flex flex-col gap-2"
                        >
                          <div
                            v-for="(item, index) in cust.customField.list"
                            :key="item"
                            class="flex items-center gap-2"
                          >
                            <SRadioGroupItem
                              :data-cy-option="stripHtml(item)"
                              :id="`radio-${cust.uuid}-${index}`"
                              :value="item"
                            />
                            <SLabel :for="`radio-${cust.uuid}-${index}`">
                              {{ item }}
                            </SLabel>
                          </div>
                        </SRadioGroup>
                      </div>
                      <!-- unique checkbox radio -->

                      <!-- list of checkbox -->
                      <div
                        v-else-if="
                          cust.customField.questionType === QuestionType.LIST
                        "
                      >
                        <div data-cy="cf-list" class="flex flex-col gap-2">
                          <label
                            v-for="item in cust.customField.list"
                            :key="item"
                            class="flex cursor-pointer items-center gap-2"
                          >
                            <SCheckbox
                              :data-cy-option="stripHtml(item)"
                              :model-value="isListChecked(cust, item)"
                              @update:model-value="
                                (checked) =>
                                  toggleListAnswer(cust, item, checked)
                              "
                            />
                            <span>{{ stripHtml(item) }}</span>
                          </label>
                        </div>
                      </div>
                      <!-- checkbox -->
                      <span
                        class="mt-1 block text-sm text-destructive"
                        data-cy="cf-required-error"
                        v-if="
                          !cust.answer &&
                          cust.customField.validation ===
                            CustomFieldValidation.MANDATORY &&
                          canValidate
                        "
                      >
                        {{ $t("custom-fields.required-field") }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SDialogFooter
          class="shrink-0 items-center justify-between gap-3 border-t px-6 py-4"
        >
          <p class="text-sm text-muted-foreground">
            {{ $t("custom-fields.footer-text") }}
          </p>
          <div class="flex justify-end gap-2">
            <SButton
              type="button"
              variant="outline"
              @click="hideDialog"
              data-cy="custom-fields-cancel-button"
            >
              {{ $t("button.cancel") }}
            </SButton>
            <SButton
              type="button"
              :loading="isLoading"
              @click="proceed"
              data-cy="custom-fields-confirm-button"
            >
              {{ $t("button.confirm") }}
            </SButton>
          </div>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>
<script lang="ts">
import { ModalComponents, ModalStatus } from "@/constants/modal";
import ModalModule from "@/store/modules/modal";
import {
  CustomFieldType,
  QuestionType,
  InformationType,
  ICustomFieldAnswer,
  CustomFieldValidation,
} from "@/types/db/product-category";
import {
  bookingCustomFields,
  startProcessing,
} from "@/utils/vue-helpers/booking";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import MazPhoneNumberInput from "maz-ui/components/MazPhoneNumberInput";
import { parsePhoneNumber } from "libphonenumber-js";
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { cloneDeep, filter } from "lodash";
import CommonModule from "@/store/modules/common";
import BookingModule from "@/store/modules/booking";
import { createCompositeId } from "@/utils/helpers/app";
import dayjs from "dayjs";
import { insertFile } from "@/utils/helpers/db";
import binSvg from "@/assets/images/svg/bin-svgrepo-com.svg";
import SmeetzCropper from "./SmeetzCropper.vue";
import { getLanguageList } from "@/api/common/common";
import CustomersModule from "@/store/modules/customers";
import POSConfigModule from "@/store/modules/pos-config";
import AppModule from "@/store/modules/app";
import {
  SButton,
  SCheckbox,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SInput,
  SLabel,
  SRadioGroup,
  SRadioGroupItem,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
  STextarea,
} from "@smeetz/ui-core";
import { SDatePicker } from "@smeetz/ui-components/s-date-picker";
import { SCountrySelect } from "@smeetz/ui-components/s-country-select";
import { parseDate } from "@internationalized/date";

enum ValidationDataType {
  Number = 1,
  Email = 2,
}
export default defineComponent({
  name: ModalComponents.CustomFields,
  components: {
    MazPhoneNumberInput,
    SmeetzCropper,
    SButton,
    SCheckbox,
    SCountrySelect,
    SDatePicker,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    SInput,
    SLabel,
    SRadioGroup,
    SRadioGroupItem,
    SSelect,
    SSelectContent,
    SSelectItem,
    SSelectTrigger,
    SSelectValue,
    STextarea,
  },
  async setup() {
    const { t } = useI18n({});
    const phoneValidationResults = ref();
    const dialogVisible = dialogVisibleForStatus(ModalStatus.CustomFields);
    const isLoading = ref<boolean>(false);
    const canValidate = ref<boolean>(false);
    const selectedFiles: any[] = [];
    const cropperRef = ref(null);
    const bookingTypeCustomFields = ref<ICustomFieldAnswer[]>([]);
    const priceCategoryData = ref<
      {
        priceId: number;
        priceName: string;
        categoryId: number;
        categoryName: string;
      }[]
    >([]);

    const binIcon = binSvg;
    const binAlt = "bin";
    const binIconStyle = "width: 18px; height: 18px;";

    // lets assign bookingTypeCustomFields the custom fields already attached to the booking
    if (bookingCustomFields.value) {
      // Why clonning? We don't need the UI to be attached directly to answers of the booking and automatically be updated
      // we need the user to confirm his answers so we can attach them to the booking
      bookingTypeCustomFields.value = cloneDeep(
        bookingCustomFields.value?.filter(
          (cust) => cust.customField.customFieldType === CustomFieldType.BOOKING
        )
      );
      console.log("------------- bookingTypeCustomFields.value");
      console.log(bookingTypeCustomFields.value);
      // Now lets iterate thru BookingCustomFields and check for
      // unanswered fields of type (firstName, lastName, Email, Address, Phone)
      // and see of the current booking has a customer assigned to it so that we prefill those unanswered fields
      if (CustomersModule.getSelectedCustomerId) {
        // So there is a customer assigned to this booking
        // Lets check for unanswered booking custom fields that can be prefilled with customer data
        for (let i = 0; i < bookingTypeCustomFields.value.length; i++) {
          if (!bookingTypeCustomFields.value[i].answer) {
            // This is an unanswered field
            if (
              bookingTypeCustomFields.value[i].customField.informationType ===
              InformationType.FIRSTNAME
            ) {
              bookingTypeCustomFields.value[i].answer =
                CustomersModule.getSelectedCustomerFirstName;
            }
            if (
              bookingTypeCustomFields.value[i].customField.informationType ===
              InformationType.LASTNAME
            ) {
              bookingTypeCustomFields.value[i].answer =
                CustomersModule.getSelectedCustomerLastName;
            }
            if (
              bookingTypeCustomFields.value[i].customField.informationType ===
              InformationType.EMAIL
            ) {
              bookingTypeCustomFields.value[i].answer =
                CustomersModule.getSelectedCustomerEmail;
            }
            if (
              bookingTypeCustomFields.value[i].customField.informationType ===
              InformationType.ADDRESS
            ) {
              bookingTypeCustomFields.value[i].answer =
                CustomersModule.getSelectedCustomerAddress;
            }
          } else {
            continue;
          }
        }
      }
    }
    const ticketTypeCustomFields = ref<ICustomFieldAnswer[]>([]);
    const groupedTicketTypeCustomFieldsByPSS = ref<
      { pssUuid: string; customFields: ICustomFieldAnswer[] }[]
    >([]);
    // lets assign ticketTypeCustomFields the custom fields already attached to the pssList
    if (bookingCustomFields.value) {
      // Why clonning? We don't need the UI to be attached directly to answers of the booking and automatically be updated
      // we need the user to confirm his answers so we can attach them to the booking
      ticketTypeCustomFields.value = cloneDeep(
        bookingCustomFields.value?.filter(
          (cust) => cust.customField.customFieldType === CustomFieldType.TICKET
        )
      );

      // lets now fill groupedTicketTypeCustomFieldsByPSS with custom fields grouped by PSS
      for (let i = 0; i < ticketTypeCustomFields.value.length; i++) {
        if (
          groupedTicketTypeCustomFieldsByPSS.value.findIndex(
            (it) => it.pssUuid === ticketTypeCustomFields.value[i].pssUuid
          ) < 0
        ) {
          // so this custom field doesn't exist yet, lets add it and add all custom fields related to the same pss
          groupedTicketTypeCustomFieldsByPSS.value.push({
            pssUuid: String(ticketTypeCustomFields.value[i].pssUuid),
            customFields: ticketTypeCustomFields.value.filter(
              (cust) => cust.pssUuid === ticketTypeCustomFields.value[i].pssUuid
            ),
          });
        }
      }
    }

    // Lets fill in priceCategoryData
    if (BookingModule.selectedBooking?.pssList) {
      // consider only pss with custom fields
      const pssList = BookingModule.selectedBooking?.pssList.filter(
        (p) => p.customFieldsAnswers && p.customFieldsAnswers.length
      );
      for (let i = 0; i < pssList.length; i++) {
        if (
          priceCategoryData.value.findIndex(
            (it) =>
              it.categoryId === pssList[i].categoryId &&
              it.priceId === pssList[i].priceId
          ) < 0
        ) {
          priceCategoryData.value.push({
            priceId: pssList[i].priceId,
            priceName: pssList[i].priceName,
            categoryId: pssList[i].categoryId,
            categoryName: pssList[i].categoryName,
          });
        }
      }
    }

    const showPriceCategory = computed(() => {
      return (
        priceCategoryData.value &&
        priceCategoryData.value.length &&
        computedDisplayedCustomFieldsType.value === CustomFieldType.TICKET
      );
    });
    // Lets make the selected priceCategory the first item in the array priceCategoryData
    const selectedPriceCategory = ref();
    if (priceCategoryData.value && priceCategoryData.value.length) {
      selectedPriceCategory.value = priceCategoryData.value[0];
    }

    const displayedCustomFieldsType = ref(CustomFieldType.TICKET);
    if (bookingTypeCustomFields.value && bookingTypeCustomFields.value.length) {
      // Lets always display booking type custom fields first
      displayedCustomFieldsType.value = CustomFieldType.BOOKING;
    }

    const userCurrentCountry = computed(() => {
      return POSConfigModule.groupPhoneCode || "CH";
    });

    // Helper function to get country code from phone number
    const getCountryCodeFromPhone = (customField: any): string | undefined => {
      // Before checking country code from phone number answer lets check if there is an answer
      let correspondingCustomField = null;
      if (customField.type === CustomFieldType.BOOKING) {
        correspondingCustomField = bookingTypeCustomFields.value.find(
          (c) => c.uuid === customField.uuid
        );
      } else if (customField.type === CustomFieldType.TICKET) {
        correspondingCustomField = groupedTicketTypeCustomFieldsByPSS.value
          .map((g) => g.customFields)
          .flat()
          .find((c) => c.uuid === customField.uuid);
      }

      // If there is no such a custom field then we avoid checking for country code
      if (!correspondingCustomField) return undefined;
      if (correspondingCustomField) {
        // If the custom field is not originally answered then we avoid checking for country code
        // following up logic will show the user the group country for unanswered phone number custom fields
        if (!correspondingCustomField.answer) return undefined;
      }

      // So there is a corresponding custom field and it has an answer
      const phoneNumber = customField.answer;
      if (!phoneNumber) return undefined;
      try {
        const parsed = parsePhoneNumber(phoneNumber);
        return parsed?.country;
      } catch (error) {
        console.log("Error parsing phone number:", error);
        return undefined;
      }
    };

    const computedDisplayedCustomFieldsType = computed(() => {
      return displayedCustomFieldsType.value;
    });

    /**
     * User can confirm only if all required fields have been answered
     */
    const canConfirm = computed(() => {
      for (let i = 0; i < bookingTypeCustomFields.value.length; i++) {
        // check if:
        // - current field is mandatory AND
        // we have no "answer" and current field isn't a list OR
        // we have no listAnswer and current field is a list
        if (
          bookingTypeCustomFields.value[i].customField.validation ===
          CustomFieldValidation.MANDATORY
        ) {
          if (
            (!bookingTypeCustomFields.value[i].listAnswer ||
              !bookingTypeCustomFields.value[i].listAnswer.length) &&
            bookingTypeCustomFields.value[i].customField.questionType ===
              QuestionType.LIST
          ) {
            // This a LIST or a DROPDOWN and it doesn't have a listAnswer
            return false;
          }
          if (
            !bookingTypeCustomFields.value[i].answer &&
            [QuestionType.LIST].findIndex(
              (it) =>
                it === bookingTypeCustomFields.value[i].customField.questionType
            ) === -1
          ) {
            // This is not a LIST nor a DROPDOWN and it dosn't have an answer
            return false;
          }
        }
      }
      for (let i = 0; i < ticketTypeCustomFields.value.length; i++) {
        // check if:
        // - current field is mandatory AND
        // we have no "answer" and current field isn't a list OR
        // we have no listAnswer and current field is a list
        if (
          ticketTypeCustomFields.value[i].customField.validation ===
          CustomFieldValidation.MANDATORY
        ) {
          if (
            (!ticketTypeCustomFields.value[i].listAnswer ||
              !ticketTypeCustomFields.value[i].listAnswer.length) &&
            ticketTypeCustomFields.value[i].customField.questionType ===
              QuestionType.LIST
          ) {
            // This a LIST or a DROPDOWN and it doesn't have a listAnswer
            return false;
          }
          if (
            !ticketTypeCustomFields.value[i].answer &&
            [QuestionType.LIST].findIndex(
              (it) =>
                it === ticketTypeCustomFields.value[i].customField.questionType
            ) === -1
          ) {
            // This is not a LIST nor a DROPDOWN and it dosn't have an answer
            return false;
          }
        }
      }
      return true;
    });

    function isEmailValid(custAnswer: ICustomFieldAnswer) {
      const validationResult = { isValid: true, message: "" };
      if (custAnswer.customField.informationType === InformationType.EMAIL) {
        // Lets validate the email using same regew used by vuelidate
        // https://github.com/vuelidate/vuelidate/blob/%40vuelidate/validators%402.0.3/packages/validators/src/raw/email.js

        const emailRegex =
          // eslint-disable-next-line no-control-regex
          /^(?:[A-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9]{2,}(?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;

        let res = emailRegex.test(custAnswer.answer);
        if (res) {
          validationResult.isValid = true;
          validationResult.message = "";
          return validationResult;
        } else {
          validationResult.isValid = false;
          validationResult.message = "Email not valid";
          return validationResult;
        }
      }
      return validationResult;
    }
    function selectBookingTypeCustomFields() {
      displayedCustomFieldsType.value = CustomFieldType.BOOKING;
    }
    function selectTicketTypeCustomFields() {
      displayedCustomFieldsType.value = CustomFieldType.TICKET;
      selectedPriceCategory.value = priceCategoryData.value[0];
    }
    /**
     * Used to be considered in the rules array to validate the phone number
     */
    function checkPhoneNumber() {
      // We validate if there is avalue entered by the user
      if (phoneValidationResults.value) {
        if (phoneValidationResults.value.nationalNumber)
          return phoneValidationResults.value.isValid;
      }
      // // We don't let the phone validator we're using assign undefined value to phone field when it has empty string
      // // beause it will never be considered and the user will never be able to empty a phone number from a customer
      // if (!ruleForm.phone) ruleForm.phone = "";
      // We don't validate if there is no phone entered
      return true;
    }

    function languagesList(customFieldList: string[]) {
      const languages = CommonModule.languages;
      const filteredLanguages: { key: string; value: string }[] = [];
      languages
        .filter((lang) => customFieldList.includes(lang.code))
        .forEach((lang) => {
          filteredLanguages.push({
            key: lang.code,
            value: lang.name,
          });
        });
      return filteredLanguages;
    }

    function selectPriceCategory(priceCategory: {
      priceId: number;
      priceName: string;
      categoryId: number;
      categoryName: string;
    }) {
      selectedPriceCategory.value = priceCategory;
    }

    function handleFileUpload(
      event: any,
      customFieldUuid: string,
      type: CustomFieldType
    ) {
      const file = event.target.files[0];

      // Now lets fill the file name into 'answer' field based on whether we are in Ticket custom fields or Booking custom fields
      if (type === CustomFieldType.BOOKING) {
        const fileField = bookingTypeCustomFields.value.find(
          (c) => c.uuid === customFieldUuid
        );
        if (fileField) {
          fileField.isInvalid = false;
          fileField.validationMessage = "";
          const fileSize = file.size;
          if (fileSize > 10485760) {
            fileField.isInvalid = true;
            fileField.validationMessage = "File should be less than 10 mb";
            return;
          }
          fileField.answer = file.name;
          fileField.fileID = createCompositeId([
            dayjs().toISOString(),
            file.name,
          ]);

          const reader = new FileReader();
          reader.onload = async (ev: any) => {
            const fileData = ev.target.result;
            const blob = new Blob([file], { type: file.type });
            // Lets keep a url for the image so that it can be displayed
            fileField.fileUrl = URL.createObjectURL(blob);

            selectedFiles.push({
              id: fileField.fileID,
              file: blob,
            });
          };
          reader.readAsBinaryString(file);
        }
      }
      if (type === CustomFieldType.TICKET) {
        const fileField = groupedTicketTypeCustomFieldsByPSS.value
          .map((g) => g.customFields)
          .flat()
          .find((c) => c.uuid === customFieldUuid);

        if (fileField) {
          fileField.isInvalid = false;
          fileField.validationMessage = "";
          const fileSize = file.size;
          if (fileSize > 10485760) {
            fileField.isInvalid = true;
            fileField.validationMessage = "File should be less than 10 mb";
            return;
          }
          fileField.answer = file.name;
          fileField.fileID = createCompositeId([
            dayjs().toISOString(),
            file.name,
          ]);

          const reader = new FileReader();
          reader.onload = async (ev: any) => {
            const fileData = ev.target.result;
            const blob = new Blob([file], { type: file.type });
            // Lets keep a url for the image so that it can be displayed
            fileField.fileUrl = URL.createObjectURL(blob);

            selectedFiles.push({
              id: fileField.fileID,
              file: blob,
            });
          };
          reader.readAsBinaryString(file);
        }
      }
    }

    function removeFile(cust: ICustomFieldAnswer) {
      cust.fileUrl = "";
      cust.fileID = "";
      cust.answer = "";
      cust.isCropped = false;
    }

    function confirmCroppedPicture(cust: ICustomFieldAnswer) {
      console.log("this.$refs.cropperRef");
      console.log(this.$refs[cust.uuid + "_cropper"]);
      console.log("this.$refs[cust.uuid+'_cropper'][0]");
      console.log(this.$refs[cust.uuid + "_cropper"][0]);
      ////
      // THIS IS BASE 64 IMAGE
      // const croppedData = this.$refs.cropperRef[0].getCroppedCanvas().toDataURL();
      // console.log("croppedData");
      // console.log(croppedData);
      ////
      // THIS IS BASE 64 IMAGE
      // console.log("going to export the image");
      // const img = (this.$refs.cropperRef[0] as any).getCroppedCanvas().toDataURL('image/jpeg');
      // console.log("done exporting the image");
      // console.log("img");
      // console.log(img);
      ////

      // Get the cropped file as blob, And push it to selectedFiles array, And display it to the user, And destroy cropper
      (this.$refs[cust.uuid + "_cropper"][0] as any)
        .getCroppedCanvas()
        .toBlob((blob: any) => {
          const cropedFile: any = new File(
            [blob],
            String(cust.fileID + "hola")
          );
          // const fileField = bookingTypeCustomFields.value.find(
          //   (c) => c.uuid === cust.uuid
          // );
          const fileField = cust;

          if (fileField) {
            fileField.fileID = createCompositeId([dayjs().toISOString()]);
            fileField.answer = fileField.fileID;
            // Lets keep a url for the image so that it can be displayed
            fileField.fileUrl = URL.createObjectURL(blob);
            selectedFiles.push({
              id: fileField.fileID,
              file: cropedFile,
            });
            cust.fileUrl = fileField.fileUrl;
            cust.isCropped = true;
          }
        }, "image/jpeg");
      // Lets now destroy the cropper
      (this.$refs[cust.uuid + "_cropper"][0] as any).destroy();
      // Hide cropping are and keep only the img element displaying the cropped image
      //cust.uuid+'_cropper'
      const cropperParent = this.$refs[cust.uuid + "_cropper_parent"][0];
      // hiding cropper parent
      cropperParent.style.display = "none";
      ////
    }

    function cancelCroppedPicture(cust: ICustomFieldAnswer) {
      cust.answer = "";
      cust.fileID = "";
      cust.fileUrl = "";
    }

    function stripHtml(val: string): string {
      return val.replace(/<[^>]+>/g, "").trim();
    }

    // A stored answer can hold a value the picker cannot parse, and parseDate throws
    // during render, which leaves the dialog empty behind its overlay (L3-8180)
    function dateAnswerValue(answer: string) {
      try {
        return answer ? parseDate(answer) : undefined;
      } catch {
        return undefined;
      }
    }

    const appLanguage = computed(() => AppModule.language);

    return {
      t,
      isLoading,
      dialogVisible,
      bookingTypeCustomFields,
      ticketTypeCustomFields,
      selectBookingTypeCustomFields,
      selectTicketTypeCustomFields,
      QuestionType,
      InformationType,
      languagesList,
      userCurrentCountry,
      canConfirm,
      CustomFieldValidation,
      displayedCustomFieldsType,
      computedDisplayedCustomFieldsType,
      CustomFieldType,
      priceCategoryData,
      selectedPriceCategory,
      showPriceCategory,
      selectPriceCategory,
      groupedTicketTypeCustomFieldsByPSS,
      handleFileUpload,
      selectedFiles,
      binIcon,
      binAlt,
      binIconStyle,
      removeFile,
      ValidationDataType,
      isEmailValid,
      canValidate,
      confirmCroppedPicture,
      cancelCroppedPicture,
      appLanguage,
      getCountryCodeFromPhone,
      stripHtml,
      dateAnswerValue,
    };
  },
  methods: {
    // Multi-select list answers are stored as an array on cust.listAnswer.
    // the design-system checkbox is a single boolean, so membership is managed here.
    isListChecked(cust: ICustomFieldAnswer, item: string): boolean {
      return Array.isArray(cust.listAnswer) && cust.listAnswer.includes(item);
    },
    toggleListAnswer(
      cust: ICustomFieldAnswer,
      item: string,
      checked: boolean | "indeterminate"
    ) {
      if (!Array.isArray(cust.listAnswer)) cust.listAnswer = [];
      if (checked === true) {
        if (!cust.listAnswer.includes(item)) cust.listAnswer.push(item);
      } else {
        cust.listAnswer = cust.listAnswer.filter((x: string) => x !== item);
      }
    },
    hideDialog() {
      ModalModule.setStatus(ModalStatus.Hidden);
    },
    async proceed() {
      // If We are now displaying Booking custom fields, if there are Ticket custom fields, then we need to display tickets custom fields
      // If we are now displaying Ticket custom fields, if we are now displaying the last category custom fields, then we need to call proceedAnswers
      // If we are now displaying Ticket custom fields but we are not displaying the last category custom fields, then we need to select the next category
      if (this.displayedCustomFieldsType === CustomFieldType.BOOKING) {
        // If we have Tickets custom fields then we show them, otherwise we start processing
        if (this.ticketTypeCustomFields && this.ticketTypeCustomFields.length) {
          this.selectTicketTypeCustomFields();
        } else {
          await this.proceedAnswers();
        }
      } else if (this.displayedCustomFieldsType === CustomFieldType.TICKET) {
        // If we are displaying the last category then we will start processing, otherwise we display the next category
        const selectedPriceCat = this.selectedPriceCategory;
        const index = this.priceCategoryData.findIndex(
          (p: any) =>
            p.priceId === selectedPriceCat.priceId &&
            p.categoryId === selectedPriceCat.categoryId
        );
        if (index > -1) {
          if (index === this.priceCategoryData.length - 1) {
            // So we are actually displaying the last priceCategory, lets start processing
            await this.proceedAnswers();
          } else {
            // We are not displaying the last price category, lets select the next one
            const nextPriceCat = this.priceCategoryData[index + 1];
            this.selectPriceCategory(nextPriceCat);
          }
        }
      }
    },
    async proceedAnswers() {
      console.log("----------- hi from proceedAnswers");
      this.canValidate = true;
      if (!this.canConfirm) {
        // If one of the mandatory field is not filled we don't confirm answers, and user will see red validation msgs in UI
        return;
      }
      // 1- Confirm custom fields answers
      await this.confirmAnswers();
      // 2- Start procesing
      await startProcessing();
      // 3- Hide the popup
      ModalModule.setStatus(ModalStatus.Hidden);
    },
    async confirmAnswers() {
      // TODO Hamza: Grab the user input as answers for questions and assign them to customFieldsAnswers in IPSB/IPSS, and update the booking
      // For date answer don't forget to convert it into isostring.
      const bookingCopy = cloneDeep(BookingModule.selectedBooking);
      if (bookingCopy) {
        // Lets attach custom fields answers for the booking obj
        if (
          bookingCopy.customFieldsAnswers &&
          bookingCopy.customFieldsAnswers.length
        ) {
          for (let i = 0; i < this.bookingTypeCustomFields.length; i++) {
            // if (!bookingCopy.customFieldsAnswers || !bookingCopy.customFieldsAnswers[i]) continue;
            // TODO Hamza: if answer is actually a date then we need to get its utc.isostring format and assign it to booking field answer
            const customFieldsAnswers = bookingCopy.customFieldsAnswers.find(
              (c) => c.uuid === this.bookingTypeCustomFields[i].uuid
            );
            if (customFieldsAnswers) {
              customFieldsAnswers.answer =
                this.bookingTypeCustomFields[i].answer;
              if (Array.isArray(this.bookingTypeCustomFields[i].listAnswer)) {
                // So this should be a list of checkboxes answers
                customFieldsAnswers.listAnswer =
                  this.bookingTypeCustomFields[i].listAnswer;
              } else {
                // so this should be a dropdown/radio, we need to store its answer in 'answer' field, not 'listAnswer' field
                customFieldsAnswers.answer =
                  this.bookingTypeCustomFields[i].listAnswer;
              }

              // If this field is of type file then we need the fileID field as well and insert it into the db
              if (
                customFieldsAnswers.customField.questionType ===
                  QuestionType.FILE &&
                this.bookingTypeCustomFields[i].fileID &&
                customFieldsAnswers.fileID !==
                  this.bookingTypeCustomFields[i]
                    .fileID /*Because we only insert a new file in dexiedB, we don't try to insert again if no new file was uploaded*/
              ) {
                // so going to confirm file
                customFieldsAnswers.fileID =
                  this.bookingTypeCustomFields[i].fileID;
                customFieldsAnswers.fileUrl =
                  this.bookingTypeCustomFields[i].fileUrl;
                customFieldsAnswers.isCropped =
                  this.bookingTypeCustomFields[i].isCropped;
                const file = this.selectedFiles.find(
                  (f: any) => f.id === customFieldsAnswers.fileID
                ).file;
                await insertFile({
                  _id: customFieldsAnswers.fileID,
                  file: file,
                  customFieldId: customFieldsAnswers.customField.id,
                });
              }
            }
          }
        }

        // Lets attach custom fields answers for the pss list
        for (let j = 0; j < bookingCopy.pssList.length; j++) {
          if (
            bookingCopy.pssList[j].customFieldsAnswers &&
            bookingCopy.pssList[j].customFieldsAnswers?.length
          ) {
            for (let h = 0; h < this.ticketTypeCustomFields.length; h++) {
              // TODO Hamza: if answer is actually a date then we need to get its utc.isostring format and assign it to booking field answer
              const customFieldsAnswers = bookingCopy.pssList[
                j
              ].customFieldsAnswers?.find(
                (c) => c.uuid === this.ticketTypeCustomFields[h].uuid
              );
              if (customFieldsAnswers) {
                customFieldsAnswers.answer =
                  this.ticketTypeCustomFields[h].answer;
                if (Array.isArray(this.ticketTypeCustomFields[h].listAnswer)) {
                  // So this should be a list of checkboxes answers
                  customFieldsAnswers.listAnswer =
                    this.ticketTypeCustomFields[h].listAnswer;
                } else {
                  // so this should be a dropdown/radio, we need to store its answer in 'answer' field, not 'listAnswer' field
                  customFieldsAnswers.answer =
                    this.ticketTypeCustomFields[h].listAnswer;
                }

                // If this field is of type file then we need the fileID field as well and insert it into the db
                if (
                  customFieldsAnswers.customField.questionType ===
                    QuestionType.FILE &&
                  this.ticketTypeCustomFields[h].fileID &&
                  customFieldsAnswers.fileID !==
                    this.ticketTypeCustomFields[h]
                      .fileID /*Because we only insert a new file in dexiedB, we don't try to insert again if no new file was uploaded*/
                ) {
                  customFieldsAnswers.fileID =
                    this.ticketTypeCustomFields[h].fileID;
                  customFieldsAnswers.fileUrl =
                    this.ticketTypeCustomFields[h].fileUrl;
                  customFieldsAnswers.isCropped =
                    this.ticketTypeCustomFields[h].isCropped;
                  const file = this.selectedFiles.find(
                    (f: any) => f.id === customFieldsAnswers.fileID
                  ).file;
                  await insertFile({
                    _id: customFieldsAnswers.fileID,
                    file: file,
                    customFieldId: customFieldsAnswers.customField.id,
                  });
                }
              }
            }
          }
        }
        BookingModule.updateBooking(bookingCopy);
      }
    },
  },
});
</script>
<style lang="scss">
.confirm-button-container {
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
